import {useEffect, useMemo, useRef, useState} from 'react'
import {Styles} from './Select'

type MultiSelectProps = {
  label?: string
  name?: string
  placeholder: string
  options: {value: any; label: string}[]
  selected?: {value: any; label: string}[]
  onSelect: (values: {value: any; label: string}[]) => void
  disabled?: boolean
  required?: boolean
  searchFn?: (val: string) => void
  createFn?: (val: string) => void
  error?: boolean
  className?: string
}

export const MultiSelect = (props: MultiSelectProps) => {
  const [selected, setSelected] = useState<{value: any; label: string}[]>([])
  const [value, setValue] = useState('')
  const [localOptions, setLocalOptions] = useState<any>()
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const options = useMemo(() => {
    const source = localOptions || props.options
    return source.filter((opt: any) => !selected.some((s) => s.value === opt.value))
  }, [props.options, localOptions, selected])

  useEffect(() => {
    if (props.selected) {
      setSelected(props.selected)
    }
  }, [props.selected])

  const handleSelect = (item: {value: any; label: string}) => {
    const newSelected = selected.some((s) => s.value === item.value)
      ? selected.filter((s) => s.value !== item.value)
      : [...selected, item]

    setSelected(newSelected)
    props.onSelect(newSelected)
  }

  const localSearchFn = (val: string) => {
    setLocalOptions(
      props.options.filter((option) => option.label.toLowerCase().includes(val.toLowerCase()))
    )
  }

  const removeSelected = (valueToRemove: any) => {
    const newSelected = selected.filter((item) => item.value !== valueToRemove)
    setSelected(newSelected)
    props.onSelect(newSelected)
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  return (
    <>
      <Styles />
      <div className='select-container' ref={dropdownRef}>
        {/* Input Box */}
        <div className='select-input' onClick={() => !props.disabled && setIsOpen(!isOpen)}>
          <div className='selected-tags'>
            {selected.map((item) => (
              <div key={item.value} className='selected-tag'>
                {item.label}
                <span
                  className='selected-tag-remove'
                  onClick={(e) => {
                    e.stopPropagation()
                    removeSelected(item.value)
                  }}
                >
                  ×
                </span>
              </div>
            ))}
          </div>
          <input
            className='select-form-control'
            placeholder={selected.length === 0 ? props.placeholder : ''}
            value={value}
            disabled={props.disabled}
            onChange={(e) => {
              const val = e.target.value
              props.searchFn ? props.searchFn(val) : localSearchFn(val)
              setValue(val)
            }}
            onClick={(e) => {
              e.stopPropagation()
              if (!props.disabled) setIsOpen(true)
            }}
          />
          <i className={`bi ${isOpen ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
        </div>
        {/* Dropdown List */}
        {isOpen && (
          <div className='select-menu rounded border border-gray-300 shadow-lg'>
            {options.length > 0 ? (
              options.map((item: any) => (
                <div
                  key={item.value}
                  className={`select-item ${
                    selected.some((s) => s.value === item.value)
                      ? 'bg-primary/10 text-primary font-medium'
                      : ''
                  }`}
                  onClick={() => handleSelect(item)}
                >
                  {item.label}
                  {selected.some((s) => s.value === item.value) && <i className='bi bi-check'></i>}
                </div>
              ))
            ) : (
              <>
                <div className='px-4 py-2 text-gray-500'>No record</div>
                {props.createFn && value && (
                  <div
                    className='px-4 py-2 cursor-pointer'
                    onClick={() => {
                      props.createFn?.(value)
                      setIsOpen(false)
                    }}
                  >
                    Create "{value}"
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </>
  )
}
